import { OnInit, AfterViewInit, Component, input, output, effect, signal, computed, HostListener } from '@angular/core';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { SoChartDataset, SoDoughnutChartDataset } from '../interfaces/chart.interfaces';
import { registerables } from 'chart.js';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
Chart.register(...registerables, ChartDataLabels);

@Component({
  selector: 'so-chart-ui',
  standalone: true,
  imports: [ DecimalPipe, NgTemplateOutlet ],
  templateUrl: './so-chart-ui.component.html',
  styleUrl: './so-chart-ui.component.scss',
})
export class SoChartUI implements OnInit, AfterViewInit {
  dataset = input.required<SoChartDataset>();
  responsive = input<boolean>(false);
  displayLegends = input<boolean>(true);
  legendPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');
  displayLabels = input<boolean>(true);
  displayGroupLabels = input<boolean>(true);
  useLogarithmicScale = input<boolean>(true);
  labelsMinDisplayValue = input<number>(0);
  beginAtZero = input<boolean>(true);
  // Formateador opcional de los ticks del eje de valores (Y en line/bar, X en horizontalBar).
  // Si se provee, reemplaza el render por defecto (útil p. ej. para notación compacta: 30M, 1.5K).
  yTickFormatter = input<((value: number) => string) | null>(null);
  // Formateador opcional del valor en el tooltip (independiente del eje). Si se provee,
  // reemplaza el texto del valor en el tooltip (p. ej. separadores de miles: 25,400,000).
  tooltipValueFormatter = input<((value: number) => string) | null>(null);
  // Interacción "de columna" (opt-in): el tooltip aparece al pasar por cualquier punto de
  // la columna (no sólo justo sobre el punto) y se ubica cerca del cursor.
  nearestTooltip = input<boolean>(false);
  pointRadius = input<number>(5);
  minRows = input<number>(10);
  maxValues = input<number>(-1);
  barPercentage = input<number>(1);
  categoryPercentage = input<number>(1);
  cutout = input<string>('76%');
  labelsAs = input<'values' | 'percentages'>('values');
  labelFontSize = input<string>('14px');
  width = input<string>('100%');
  height = input<string>('100%');
  margin = input<string>('0');
  padding = input<string>('0');
  useCustomLegends = input<boolean>(true);
  legendShowDetails = input<boolean>(false);
  legendColumns = input<number>(0);
  fontSize = input<number>(15);
  fontWeight = input<number>(400);
  enableLabelClickEvent = input<boolean>(false);
  maxVisibleLegends = input<number>(0);
  activeLegendIndex = input<number | null>(null);
  enableSegmentHighlight = input<boolean>(false);

  labelClicked = output<{ index: number; label: string }>();

  public readonly CHART_ID: string = 'chart_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
  private readonly colorRegEx: RegExp = /rgb\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)/;
  private viewIsReady = signal<boolean>(false);
  private chart: any = null;
  private ticksColor: string = 'var(--so-neuter-thick)';
  private gridColor: string = 'var(--so-gray)';
  private pointBorderColor: string = 'var(--so-white)';
  private hiddenDatasets = signal<Set<number>>(new Set());
  public overflowOpen = signal(false);

  legendItems = computed(() => {
    const ds = this.dataset();

    if (!this.useCustomLegends() || !ds.labels.length) {
      return [];
    }

    if (ds.type === 'doughnut') {
      const data = ds.data;
      const total = data.reduce((a: number, b: number) => a + b, 0);
      const showDetails = this.legendShowDetails();

      return ds.labels.map((label: string, index: number) => {
        const value = data[index] ?? 0;
        const pct = total > 0 ? ((value / total) * 100) : 0;
        return {
          label,
          color: ds.colors?.[index] || 'rgb(200, 200, 200)',
          index,
          hidden: this.hiddenDatasets().has(index),
          value: showDetails ? value : null,
          percentage: showDetails ? pct : null,
        };
      });
    }

    return [];
  });

  visibleLegendItems = computed(() => {
    const all = this.legendItems();
    const max = this.maxVisibleLegends();
    if (max <= 0 || all.length <= max) return all;
    return all.slice(0, max);
  });

  overflowLegendItems = computed(() => {
    const all = this.legendItems();
    const max = this.maxVisibleLegends();
    if (max <= 0 || all.length <= max) return [];
    return all.slice(max);
  });

  hasActiveOverflow = computed(() => {
    const activeIdx = this.activeLegendIndex();
    if (activeIdx === null || activeIdx === undefined) return false;
    return this.overflowLegendItems().some(item => item.index === activeIdx);
  });

  overflowLabel = computed(() => {
    const overflow = this.overflowLegendItems();
    if (!overflow.length) return '';
    const activeIdx = this.activeLegendIndex();
    if (activeIdx !== null && activeIdx !== undefined) {
      const active = overflow.find(item => item.index === activeIdx);
      if (active) return active.label;
    }
    return `+${overflow.length} más`;
  });

  toggleOverflow(): void {
    this.overflowOpen.update(v => !v);
  }

  closeOverflow(): void {
    this.overflowOpen.set(false);
  }

  onOverflowItemClick(index: number): void {
    this.toggleLegendItem(index);
    this.overflowOpen.set(false);
  }

  constructor() {
    effect(() => {
      const currentDataset = this.dataset();
      
      if (this.viewIsReady() && currentDataset) {
        this.createChart();
      }
    });

    effect(() => {
      this.applySegmentHighlight();
    });
  }

  @HostListener('window:resize')
  onResize() {
    if (this.chart)
      try {
        this.chart.resize();
      }
      catch {}
  }

  ngOnInit(): void {
    Chart.defaults.font.size = this.fontSize();
    Chart.defaults.font.family = 'Montserrat';
    Chart.defaults.font.weight = this.fontWeight();
  }

  ngAfterViewInit(): void {
    this.viewIsReady.set(true);
    this.createChart();

    const html: HTMLElement | null = document.getElementsByTagName('html')[0] ?? null;
    if (!html) return;
    let theme = html.dataset['theme']?.trim().toLowerCase() ?? 'light';
    this.updateThemeColors(theme);
    new MutationObserver(() => {
      theme = html.dataset['theme']?.trim().toLowerCase() ?? theme;
      this.updateThemeColors(theme);
      this.applyTheme();
    }).observe(html, { attributeFilter: ['data-theme'] });
    this.applyTheme();
  }

  private updateThemeColors(theme: string): void {
    const computedStyle = getComputedStyle(document.documentElement);
    
    if (theme === 'dark') {
      this.ticksColor = computedStyle.getPropertyValue('--so-white').trim() || '#ffffff';
      this.gridColor = computedStyle.getPropertyValue('--so-gray').trim() || '#44444f';
      this.pointBorderColor = computedStyle.getPropertyValue('--so-white').trim() || '#ffffff';
    } else {
      this.ticksColor = computedStyle.getPropertyValue('--so-neuter-thick').trim() || '#272d3b';
      this.gridColor = computedStyle.getPropertyValue('--so-gray').trim() || '#e7e7e7';
      this.pointBorderColor = computedStyle.getPropertyValue('--so-white').trim() || '#ffffff';
    }
  }

  private applyTheme() {
    if (!this.chart) return;
    try {
      this.chart.config.options.plugins.legend.labels.color = this.ticksColor;
      this.chart.config.options.scales.x.ticks.color = this.ticksColor;
      this.chart.config.options.scales.y.ticks.color = this.ticksColor;
      this.chart.config.options.scales.x.grid.color = this.gridColor;
      this.chart.config.options.scales.y.grid.color = this.gridColor;
      this.chart.update();
    }
    catch {}
  }

  private applySegmentHighlight(): void {
    if (!this.chart) return;

    const ds = this.chart.data?.datasets?.[0];
    if (!ds) return;

    if (!this.enableSegmentHighlight()) {
      delete ds.offset;
      delete ds.borderWidth;
      delete ds.borderColor;
      this.chart.update();
      return;
    }

    const activeIdx = this.activeLegendIndex();
    const count = ds.data?.length ?? 0;
    const offsets = new Array(count).fill(0);
    const borderWidths = new Array(count).fill(2);
    const borderColors = new Array(count).fill('#ffffff');

    if (activeIdx !== null && activeIdx !== undefined && activeIdx >= 0 && activeIdx < count) {
      offsets[activeIdx] = 20;
      borderWidths[activeIdx] = 3;
    }

    ds.offset = offsets;
    ds.borderWidth = borderWidths;
    ds.borderColor = borderColors;
    this.chart.update();
  }

  private getDataObject() {
    let datasets: any = [];
    const ds = this.dataset();

    if (ds.type === 'bar' || ds.type === 'horizontalBar') {
      ds.colors.forEach((color: string, index: number) => {
        if (!this.colorRegEx.test(color))
          throw new Error(`gráfica tipo '${ds.type}': el color en el índice ${index} debe estar en formato 'rgb'.`);
      });

      datasets = [{
        label: '',
        data: ds.data,
        fill: true,
        barPercentage: this.barPercentage(),
        categoryPercentage: this.categoryPercentage(),
        backgroundColor: ds.colors.map((color: string) =>
          color.trim().replace('rgb', 'rgba').replace(')', ', ' + ds.backgroundOpacity + ')')),
      }];

      return {
        labels: ds.labels.length ? ds.labels : [''],
        datasets,
      };
    }

    if (ds.type === 'groupedBars') {
      const categoriesCount = ds.data.length > 0 ? ds.data[0].length : 0;

      if (categoriesCount > 0) {
        if (ds.groupLabels.length !== ds.data.length)
          throw new Error(`gráfica tipo '${ds.type}': la cantidad de etiquetas de grupos no coincide con la cantidad de grupos de datos. (${ds.groupLabels.length} != ${ds.data.length})`);

        ds.data.forEach((group: number[], index: number) => {
          if (group.length !== categoriesCount)
            throw new Error(`gráfica tipo '${ds.type}': la cantidad de valores del grupo ${index} no coincide con la del grupo 0. (${group.length} != ${categoriesCount})`);
        });

        if (ds.colors.length !== categoriesCount)
          throw new Error(`gráfica tipo '${ds.type}': la cantidad de colores no coincide con la cantidad de categorías. (${ds.colors.length} != ${categoriesCount})`);

        ds.colors.forEach((color: string, i: number) => {
          if (!this.colorRegEx.test(color))
            throw new Error(`gráfica tipo '${ds.type}': el color ${i} debe estar en formato 'rgb'.`);
        });

        for (let i = 0; i < categoriesCount; i++) {
          const label = ds.labels[i] || '';
          const backgroundColor: string = ds.colors[i].trim().replace('rgb', 'rgba').replace(')', ', ' + ds.backgroundOpacity + ')');
          datasets.push({
            label,
            barPercentage: this.barPercentage(),
            categoryPercentage: this.categoryPercentage(),
            backgroundColor,
            data: ds.data.map((group: number[]) => group[i]),
          });
        }
      }

      return {
        labels: ds.groupLabels.length ? ds.groupLabels : [''],
        datasets,
      };
    }

    if (ds.type === 'line') {
      datasets = ds.data.map((lineData: number[], index: number) => {
        const color = ds.colors[index];
        if (!color || !this.colorRegEx.test(color))
          throw new Error(`gráfica tipo '${ds.type}': el color en la línea ${index} debe estar en formato 'rgb'.`);

        const opacity = ds.backgroundOpacities[index] || 0.5;
        const bgColor = color.trim().replace('rgb', 'rgba').replace(')', ', ' + opacity + ')');
        return {
          label: ds.linesLabels[index],
          data: lineData,
          fill: ds.fill ? 'origin' : false,
          barPercentage: this.barPercentage(),
          categoryPercentage: this.categoryPercentage(),
          borderColor: color,
          pointBackgroundColor: color,
          backgroundColor: bgColor,
          pointBorderColor: this.pointBorderColor,
          pointBorderWidth: 2.5,
          borderWidth: 2.5,
          tension: .25,
        }
      });

      datasets.unshift({
        label: 'so-hidden',
        data: [ this.minRows() ],
        fill: false,
        borderColor: 'rgba(0, 0, 0, 0)',
        backgroundColor: 'rgba(0, 0, 0, 0)',
      });

      return {
        labels: ds.labels.length ? ds.labels : [''],
        datasets,
      };
    }

    return { labels: [''], datasets };
  }

  private getDoughnutDataObject() {
    const ds = this.dataset() as SoDoughnutChartDataset;

    ds.colors.forEach((color: string, i: number) => {
      if (!this.colorRegEx.test(color))
        throw new Error(`gráfica tipo 'doughnut': el color en el índice ${i} debe estar en formato 'rgb'.`);
    });

    return {
      labels: ds.labels,
      datasets: [{
        label: [''],
        data: ds.data,
        backgroundColor: ds.colors,
        hoverOffset: 4,
        clip: 2,
      }],
    };
  }

  private getOptionsObject() {
    const currentType = this.dataset().type;
    
    const options: any = {
      responsive: this.responsive(),
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          display: false,
          weight: 'bold',
          color: 'white',
          textStrokeColor: 'black',
          textStrokeWidth: 3,
          font: {
            weight: 'bold',
            size: this.labelFontSize(),
          },
        },
        legend: {
          display: this.displayLegends(),
          position: this.legendPosition(),
          labels: {
            color: this.ticksColor,
            filter: (item: any) => item.text !== '' && item.text !== 'so-hidden',
          },
        },
        tooltip: {
          filter: (item: any) => currentType !== 'line' || item.datasetIndex !== 0,
        },
      },
      elements: {
        point: {
          radius: this.pointRadius(),
        },
      },
      scales: {
        y: {
          beginAtZero: this.beginAtZero(),
          ticks: {
            color: this.ticksColor,
            callback: (value: any) => {
              const fmt = this.yTickFormatter();
              return fmt ? fmt(Number(value)) : ' ' + value;
            },
          },
          grid: {
            color: this.gridColor,
          },
          afterBuildTicks: (axis: { ticks: Array<{ value: number }> }) => {
            if (this.useLogarithmicScale() && (currentType === 'bar' || currentType === 'groupedBars'))
              try {
                const log10Ticks: number[] = axis.ticks.map(({ value }) => Math.log10(value));
                axis.ticks = axis.ticks.filter((_, i) =>
                  i === 0 || i === axis.ticks.length - 1 || log10Ticks[i] - log10Ticks[i - 1] >= .0669 || log10Ticks[i + 1] - log10Ticks[i] >= .0669);
              }
              catch {}
            else if (currentType === 'horizontalBar')
              axis.ticks = [];
          },
        },
        x: {
          ticks: {
            display: this.displayGroupLabels(),
            color: this.ticksColor,
            font: {
              size: this.labelFontSize()
            },
          },
          grid: {
            color: this.gridColor,
          },
          afterBuildTicks: (axis: { ticks: Array<{ value: number }> }) => {
            if (!this.useLogarithmicScale() || currentType !== 'horizontalBar')
              return;

            try {
              const log10Ticks: number[] = axis.ticks.map(({ value }) => Math.log10(value));
              axis.ticks = axis.ticks.filter((_, i) =>
                i === 0 || i === axis.ticks.length - 1 || log10Ticks[i] - log10Ticks[i - 1] >= .07 || log10Ticks[i + 1] - log10Ticks[i] >= .07);
            }
            catch {}
          },
        },
      },
    };

    if (currentType === 'bar' || currentType === 'groupedBars') {
      if (currentType === 'bar')
        options.scales.x.grid.display = false;
      if (this.useLogarithmicScale())
        options.scales.y.type = 'logarithmic';
      if (this.maxValues() > -1)
        options.scales.y.max = this.maxValues();
    }
    else if (currentType === 'horizontalBar') {
      options.indexAxis = 'y';
      if (this.useLogarithmicScale())
        options.scales.x.type = 'logarithmic';
      if (this.maxValues() > -1)
        options.scales.x.max = this.maxValues();
    }

    // Tooltip: solo se sobrescribe el valor si el consumidor provee un formateador
    // (opt-in). Sin él, se mantiene el tooltip por defecto de Chart.js.
    const tooltipFmt = this.tooltipValueFormatter();
    if (tooltipFmt) {
      options.plugins.tooltip.callbacks = {
        label: (ctx: any) => {
          const p = ctx.parsed;
          const raw = (p && typeof p === 'object') ? (p.y ?? p.x ?? 0) : Number(p);
          const value = tooltipFmt(Number(raw));
          const label = ctx.dataset?.label;
          return label ? `${label}: ${value}` : `${value}`;
        },
      };
    }

    // Interacción de columna (opt-in): el tooltip se dispara al pasar por cualquier punto
    // de la columna y se posiciona junto al cursor, en vez de exigir estar justo sobre el punto.
    if (this.nearestTooltip()) {
      options.interaction = { mode: 'index', intersect: false };
      options.plugins.tooltip.position = 'nearest';
    }

    return options;
  }

  toggleLegendItem(index: number): void {
    if (this.enableLabelClickEvent()) {
      const label = (this.dataset().labels[index] as string) ?? '';
      this.labelClicked.emit({ index, label });
      return;
    }

    const hidden = new Set(this.hiddenDatasets());
    
    if (hidden.has(index)) {
      hidden.delete(index);
    } else {
      hidden.add(index);
    }
    
    this.hiddenDatasets.set(hidden);
    
    if (this.chart && this.chart.data.datasets[0]) {
      const meta = this.chart.getDatasetMeta(0);
      if (meta.data[index]) {
        meta.data[index].hidden = hidden.has(index);
        this.chart.update();
      }
    }
  }

  private getDoughnutOptionsObject() {
    return {
      responsive: false,
      cutout: this.cutout(),
      radius: '100%',
      plugins: {
        legend: {
          display: this.useCustomLegends() ? false : this.displayLegends(),
          position: this.legendPosition(),
          labels: {
            color: this.ticksColor,
          },
        },
        datalabels: {
          display: this.displayLabels(),
          formatter: (value: any, ctx: any) => {
            if (this.labelsAs() === 'values')
              return Number(value) < this.labelsMinDisplayValue() ? '' : value;

            const total: number = ctx.chart.data.datasets[0].data.reduce((acc: any, current: any) => acc + current, 0);
            const percentage: number = Number((value * 100 / total).toFixed(2));
            return percentage < this.labelsMinDisplayValue() ? '' : `${percentage}%`.replace('.00%', '%');
          },
          weight: 'bold',
          color: 'white',
          textStrokeColor: 'black',
          textStrokeWidth: 3,
          font: {
            weight: 'bold',
            size: this.labelFontSize(),
          },
        },
      },
    };
  }

  private createChart() {
    const ds = this.dataset();
    if (ds.data.length === 0)
      return;

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    const currentType = ds.type;
    const config: any = { type: currentType.replace(/horizontalBar|groupedBars/, 'bar') };
    config['data'] = currentType !== 'doughnut' ? this.getDataObject() : this.getDoughnutDataObject();
    config['options'] = currentType !== 'doughnut' ? this.getOptionsObject() : this.getDoughnutOptionsObject();
    this.chart = new Chart(this.CHART_ID, config);
    this.chart.resize();
    this.applySegmentHighlight();
    for(let i = 1; i < 100; i += 5)
      setTimeout(() => {
        if (this.chart !== null)
          this.chart.resize();
      }, i);
  }
}
